import _WidgetBaseSP from 'more/_WidgetBaseSP';
import { compose, mdsWidget } from 'dojo/_base/declare';
import "./iCalParserTypes";

class iCalParser extends compose(_WidgetBaseSP) {

    date_keys: string[] = [
        "dtstart",
        "dtend",
        "dtstamp",
        "created",
        "lastModified",
    ];
    attendee_key: string = "attendee";
    organizer_key: string = 'organizer';

    date_only_length: number = 10;

    event_begin_key_value = 'BEGIN:VEVENT';
    calendar_end_key_value = 'END:VCALENDAR';

    timeZone_begin_key_value = 'BEGIN:VTIMEZONE';
    timeZone_end_key_value = 'END:VTIMEZONE';

    valarm_recursion_max_count = 50;

    UTC_offset: number;

    isAllDay: boolean;
    convertTimeZones: boolean = false;
    calendarTimeZone: {};
    currentCalendarTZOffset: string = null;

    foreignUTCOffset_ms: number = null;

    checkIfIsDateKey(keyValueString: string): boolean {
        return this.date_keys.indexOf(keyValueString) !== -1;
    }

    splitStringEvents (iCalEvents: string) {
        // Get array of events
        let result: any = iCalEvents.split(this.event_begin_key_value).slice(1);
        if (!result) {
            return '';
        }
        // Add missing delimiter from split to each record
        result = result.map((item: string) => `${this.event_begin_key_value}${item}`);
        return result;
    };

    getVCalendarProps (iCalString: string): string {
        return iCalString.slice(0, iCalString.indexOf(this.event_begin_key_value));
    };

    getVtimeZoneProps(iCalString: string): string {
        return iCalString.slice(iCalString.indexOf(this.timeZone_begin_key_value) + this.timeZone_begin_key_value.length, iCalString.indexOf(this.timeZone_end_key_value));
    }

    removeVAlarm(vEventsString: string, count: number = this.valarm_recursion_max_count): string {
        let eventStringResult: string = vEventsString;

        let indexOfBeginVAlarm: number = eventStringResult.indexOf('BEGIN:VALARM');
        let indexOfEndVAlarm: number = eventStringResult.indexOf('END:VALARM');

        if (indexOfBeginVAlarm !== -1 && count > 0) {
            eventStringResult =
                eventStringResult.slice(0, indexOfBeginVAlarm) +
                eventStringResult.slice(indexOfEndVAlarm + 'END:VALARM'.length);

            this.removeVAlarm(eventStringResult, count - 1);
        }
        return eventStringResult;
    };

    parseEventFromString (rawString: string): IEventParsedFromICal {
        let eventObj: any = {};

        // Format event string, merge multiline values
        let eventWithMergedRows: string[] = this.mergeRows(rawString);

        for (let stringEvent of eventWithMergedRows) {
            let keyValue: IKeyValue = this.splitToKeyValueObj(stringEvent);

            let { key, value } = keyValue;

            // Handle nested array value so it does not override with same key like ATTENDEE
            if (key === this.attendee_key) {
                eventObj[this.attendee_key] = Array.isArray(eventObj[this.attendee_key])
                    ? [...eventObj[this.attendee_key], value]
                    : [value];
            } else {
                eventObj[key] = value;
            }
        }
        return eventObj;
    };

    parseTimezoneFromString(rawString: string) {
        let timeZoneObj: any = {};

        // Format event string, merge multiline values
        let eventWithMergedRows: string[] = this.mergeRows(rawString);

        let readingStandard: boolean;
        for (let row of eventWithMergedRows) {

            let keyValue: IKeyValue = this.splitToKeyValueObj(row);
            let { key, value } = keyValue;

            if (key && value) {
                if (value.indexOf("STANDARD") != -1) {
                    readingStandard = true;
                } else if (value.indexOf("DAYLIGHT") != -1) {
                    readingStandard = false;
                }

                if (key == "end" || key == "begin") continue;

                if (readingStandard === true) {
                    timeZoneObj[key + "_standard"] = value;
                } else if (readingStandard === false) {
                    timeZoneObj[key + "_daylight"] = value;
                }
            }
        }

        return timeZoneObj;
    };

    mergeRows (stringEvent: string): string[] {
        // Split key values for every new line
        let rowsArray: string[] = stringEvent.split('\n');

        // Fix formatting with multiline values
        // Multiline values starts with empty space
        let fixedRowsArray: string[] = [];

        for (let currentRow of rowsArray) { 
            if (currentRow.length > 0) {
                // Join this row with previous row if starts with empty space
                if (currentRow[0] && currentRow[0] === ' ') {
                    // Merge previous and current row
                    let mergedRows: string =
                        fixedRowsArray[fixedRowsArray.length - 1] + currentRow;

                    // Replace last item with joined rows
                    fixedRowsArray.pop();
                    fixedRowsArray.push(mergedRows);
                } else {
                    // Just add row
                    fixedRowsArray.push(currentRow);
                }
            }
        }
        return fixedRowsArray;
    };


    splitToKeyValueObj (item: string): IKeyValue {
        // Get basic delimiter indexes
        let basicDelimiterIndex: number = item.indexOf(':');
        let nestedDelimiterIndex: number = item.indexOf(';'); 
        // Check if item has nested values
        let hasNestedValues: boolean =
            nestedDelimiterIndex !== -1 && nestedDelimiterIndex < basicDelimiterIndex;
        let key: string;
        let value: any;
        // Set keys first
        if (hasNestedValues && item.slice(0, nestedDelimiterIndex) !== 'RRULE') {
            key = this.transformKey(item.slice(0, nestedDelimiterIndex));
        } else {
            key = this.transformKey(item.slice(0, basicDelimiterIndex));
        }
        // Check if key is date parameter
        let isDateKey: boolean = this.checkIfIsDateKey(key);
        // Set values
        if (hasNestedValues && key !== 'rrule') {
            value = isDateKey
                ? item.slice(nestedDelimiterIndex + 1)
                : this.parseNestedValues(item.slice(nestedDelimiterIndex + 1));
        } else {
            value = item.slice(basicDelimiterIndex + 1);
        }
        if (isDateKey) {
            value = this.parseICalDate(key, value);
        }
        return {
            key,
            value,
        };
    };

    splitNestedValues (item: string): IKeyValue {
        let nestedValueDelimiterIndex: number = item.indexOf('=');

        return {
            key: this.transformKey(item.slice(0, nestedValueDelimiterIndex)),
            value: item.slice(nestedValueDelimiterIndex + 1),
        };
    };


    parseNestedValues (values: string): IKeyValue | string {
        let result: any = {};

        // Separate key values with ; delimiter
        let valuesArray: string[] = values.split(';');

        for (let item of valuesArray) {
            let keyValue: IKeyValue = this.splitNestedValues(item);

            let { key, value } = keyValue;

            // ** Handle exception with date in nested value ** //
            // f.e. date without time
            if (key === 'value' && value.indexOf('DATE') !== -1) {
                result = value.slice(value.indexOf('DATE'));
            } else if (value.indexOf(':mailto') !== -1) {
                result[key.toUpperCase()] = value
                    .slice(0, value.indexOf(':mailto'))
                    .replace(' ', '');
                result['mailto'] = value.slice(
                    value.indexOf(':mailto:') + ':mailto:'.length
                );
            } else {
                result[key.toUpperCase()] = value;
            }
        }
        return result;
    };

    transformKey (keyOriginal: string): string {
        let resultKey = '';
        let newKey: string = keyOriginal.toLowerCase();

        let willBeUpperCase = false;

        // Remove dashes, format to camelCase
        for (let letter of newKey) {
            let isDash: boolean = letter === '-';

            if (isDash) {
                willBeUpperCase = true;
            } else if (willBeUpperCase) {
                resultKey += letter.toUpperCase();
                willBeUpperCase = false;
            } else {
                resultKey += letter;
            }
        }
        return resultKey;
    };

    parseICalDate (key: string, iCalDate: string): any {
        // No special handling for other dates
        let isTzidDate: boolean = iCalDate.indexOf('TZID') !== -1;
        let isAllDayEvent: boolean = iCalDate.indexOf('DATE:') !== -1;

        let isSimpleDate: boolean = !isTzidDate && !isAllDayEvent;
        if (isSimpleDate) {
            return this.getBaseDate(iCalDate);
        }

        if (isAllDayEvent) {
            this.isAllDay = isAllDayEvent
            let baseDate: string = iCalDate.slice(
                iCalDate.indexOf('DATE:') + 'DATE:'.length
            );
            let year: string = baseDate.slice(0, 4);
            let month: string = baseDate.slice(4, 6);
            let day: string = baseDate.slice(6, 8);

            return { value: `${year}-${month}-${day}`, isAllDay: true };
        }

        if (isTzidDate) {
            let timezone: string = iCalDate.split(':')[0];
            let baseDate: string = iCalDate.split(':')[1];

            return {
                value: this.getBaseDate(baseDate),
                timezone: timezone.slice(timezone.indexOf('TZID=') + 'TZID='.length),
            };
        }
        return { value: this.getBaseDate(iCalDate) };
    };

    getBaseDate(date: string): string {

        let baseDate: string = date;

        let year: string = baseDate.slice(0, 4);
        let month: string = baseDate.slice(4, 6);
        let day: string = baseDate.slice(6, 8);
        let hour: string = baseDate.slice(9, 11);
        let minute: string = baseDate.slice(11, 13);

        let localDateString: string = `${year}-${month}-${day}T${hour}:${minute}:00`;; // local Date string to display
        let localDateUser: Date;

        if (this.convertTimeZones === true) { 

            if (this.currentCalendarTZOffset) {
                let sign = this.currentCalendarTZOffset.slice(0, 1);
                let offsetComponents = this.currentCalendarTZOffset.split(":");
                let offsetHours = Number(offsetComponents[0]);
                let offsetMinutes = Number(offsetComponents[1]);
                let offsetMs = Math.abs(offsetHours * 60 * 60 * 1000) + Math.abs(offsetMinutes * 60 * 1000);
                
                let localDate_calender = new Date(`${year}-${month}-${day}T${hour}:${minute}:00`);

                let Zdate: Date;
                
                if (sign == "+") {
                    Zdate = new Date(localDate_calender.getTime() - offsetMs);
                } else {
                    Zdate = new Date(localDate_calender.getTime() + offsetMs);
                }

                if (this.foreignUTCOffset_ms) {
                    localDateUser = new Date(Zdate.getTime() + this.foreignUTCOffset_ms);
                    localDateString = this.toStringISO(localDateUser);
                } else {
                    localDateUser = new Date(this.toStringISO(Zdate) + ".000Z")
                    localDateString = this.toStringISO(localDateUser);
                }
            }
        }

        return localDateString // YYYY-MM-DDTHH:mm:ss
    };

    setUTC_offset(date: Date, tz: string): void {

        let date_localString: string = date.toLocaleString("ja", { timeZone: tz });
        let ParamsStr: Array<string> = date_localString.split(/[:\s\/]/);
        let ParamsNum: Array<number> = ParamsStr.map(function (value: string): number { return Number(value); });
        ParamsNum[1]--;

        let utc_number: number = Date.UTC.apply(null, ParamsNum);
        let locale_number: number = date.setMilliseconds(0);
        this.UTC_offset = (utc_number - locale_number);

    }

    parseFromICal(iCalStringEvent: string): ICalObject {

        if (iCalStringEvent) {
            // Get vcalendar props
            let vCalendarString: string = this.getVCalendarProps(iCalStringEvent);

            let sCalendarTimezone: string = this.getVtimeZoneProps(iCalStringEvent);
            this.calendarTimeZone = this.parseTimezoneFromString(sCalendarTimezone);
            if (new Date(this.calendarTimeZone["dtstart_daylight"]) < new Date() && new Date() < new Date(this.calendarTimeZone["dtstart_standard"])) {
                let hours: string = <string>this.calendarTimeZone["tzoffsetfrom_standard"].slice(0, 3);
                let minutes: string = <string>this.calendarTimeZone["tzoffsetfrom_standard"].slice(3);
                this.currentCalendarTZOffset = hours + ":" + minutes; // sommerZeit +02:00
            } else {
                let hours: string = <string>this.calendarTimeZone["tzoffsetfrom_daylight"].slice(0, 3);
                let minutes: string = <string>this.calendarTimeZone["tzoffsetfrom_daylight"].slice(3);
                this.currentCalendarTZOffset = hours + ":" + minutes; // winterZeit +01:00
            }
            if (this.convertTimeZones) console.log("current Calendar TZ Offset: ", this.currentCalendarTZOffset);
            // Get events
            let vEventsString: string = iCalStringEvent.slice(
                vCalendarString.length,
                iCalStringEvent.length - this.calendar_end_key_value.length
            );
            // Remove valarms //TODO add in future?

            vEventsString = this.removeVAlarm(vEventsString);
            // Split string events to array
            let vEventsArray: string[] = this.splitStringEvents(vEventsString);
            // Parse each event to obj
            let events: IEventParsedFromICal[] = vEventsArray.map(
                (stringEvent: string) => this.parseEventFromString(stringEvent)
            );

            return {
                calendar: {
                    begin: 'VCALENDAR',
                    prodid: 'abc',
                    version: '1',
                    end: 'VCALENDAR',
                    timezone: this.calendarTimeZone
                },
                events,
            };
        } 
        return null;
    };

    toStringISO(date: Date): string {

        if (date && Object.prototype.toString.call(date) === "[object Date]") {

            if (isNaN(date.getFullYear())) {
                return null;
            }

            let day = ("0" + date.getDate()).slice(-2); //DD
            let month = ("0" + (date.getMonth() + 1)).slice(-2); //MM
            let year = date.getFullYear(); //YYYY
            let hours = ("0" + date.getHours()).slice(-2); // HH
            let min = ("0" + date.getMinutes()).slice(-2);  // mm
            let sec = ("0" + date.getSeconds()).slice(-2); // ss

            // YYYY-MM-DDTHH:mm:ss
            let ISOString = year + "-" + month + "-" + day + "T" + hours + ":" + min + ":" + sec;
            return ISOString;
        }

        return null;
    }

    postCreate(): void {
        super.postCreate.apply(this, arguments as any);
    }
    startup(): void {
        super.startup.apply(this, arguments as any);
    }

}

export default mdsWidget(iCalParser);
